import { useState, useEffect } from "react";
import { Label } from "@/components/ui/label";
import { X, Loader2, DollarSign, BarChart3, Users, Sparkles } from "lucide-react";
import { getModelDisplayName } from "@/models";

function formatCost(value: number): string {
  return `$${value.toFixed(2)}`;
}

interface PeriodStats {
  total: number;
  count: number;
  byModel: Record<string, { total: number; count: number }>;
}

interface SpendingData {
  today: PeriodStats;
  thisWeek: PeriodStats;
  thisMonth: PeriodStats;
  last7Days: PeriodStats;
  last30Days: PeriodStats;
  allTime: PeriodStats;
  smartPrompt?: Record<string, PeriodStats>;
}

interface AdminUserSpending {
  username: string;
  spending: SpendingData;
}

type AdminSpendingData = Record<string, AdminUserSpending>;

interface SpendingStatsProps {
  onClose: () => void;
  isAdmin: boolean;
}

const PERIOD_LABELS: { key: keyof SpendingData; label: string }[] = [
  { key: "today", label: "Danas" },
  { key: "thisWeek", label: "Ova sedmica" },
  { key: "thisMonth", label: "Ovaj mjesec" },
  { key: "last7Days", label: "Zadnjih 7 dana" },
  { key: "last30Days", label: "Zadnjih 30 dana" },
  { key: "allTime", label: "Ukupno" },
];

export function SpendingStats({ onClose, isAdmin }: SpendingStatsProps) {
  const [spending, setSpending] = useState<SpendingData | null>(null);
  const [adminSpending, setAdminSpending] = useState<AdminSpendingData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const promises: Promise<void>[] = [
          fetch("/api/spending")
            .then((res) => (res.ok ? res.json() : null))
            .then((data) => setSpending(data)),
        ];

        if (isAdmin) {
          promises.push(
            fetch("/api/admin/spending")
              .then((res) => (res.ok ? res.json() : null))
              .then((data) => setAdminSpending(data))
          );
        }

        await Promise.all(promises);
      } catch {
        // ignore
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isAdmin]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-6 backdrop-blur-sm"
      style={{ backgroundColor: "var(--effect-overlay-bg)" }}
      onClick={onClose}
    >
      <div
        className="rounded-2xl border border-border w-full max-w-2xl max-h-[80vh] flex flex-col backdrop-blur-xl"
        style={{
          backgroundColor: "var(--effect-glass-surface-strong)",
          boxShadow: "var(--effect-glow-lg)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
          <h2 className="font-display text-lg font-bold flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-primary" />
            Potrošnja
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-6 space-y-6">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : spending ? (
            <>
              {/* Time period cards */}
              <div className="space-y-2">
                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                  <BarChart3 className="w-3.5 h-3.5" />
                  Pregled korištenja
                </Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {PERIOD_LABELS.map(({ key, label }) => {
                    const period = spending[key];
                    const modelEntries = Object.entries(period.byModel);
                    return (
                      <div
                        key={key}
                        className="rounded-xl border border-border bg-secondary/20 p-3 space-y-2"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                            {label}
                          </span>
                        </div>
                        <div className="flex items-baseline gap-2">
                          <span className="text-lg font-bold text-primary tabular-nums">
                            {formatCost(period.total)}
                          </span>
                          <span className="text-[11px] text-muted-foreground">
                            {period.count} {period.count === 1 ? "slika" : "slika"}
                          </span>
                        </div>
                        {modelEntries.length > 0 && (
                          <div className="space-y-1 pt-1 border-t border-border/50">
                            {modelEntries.map(([modelKey, modelStats]) => (
                              <div key={modelKey} className="flex items-center justify-between text-[10px]">
                                <span className="text-muted-foreground truncate mr-2">
                                  {getModelDisplayName(modelKey)}
                                </span>
                                <span className="text-foreground/70 tabular-nums shrink-0">
                                  {formatCost(modelStats.total)} ({modelStats.count})
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Smart Prompt costs */}
              {spending.smartPrompt?.allTime && spending.smartPrompt.allTime.count > 0 && (
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5" />
                    Korištenje Smart Prompta
                  </Label>
                  <div className="rounded-xl border border-border bg-secondary/20 p-3 space-y-2">
                    <div className="flex items-baseline gap-2">
                      <span className="text-lg font-bold text-primary tabular-nums">
                        {formatCost(spending.smartPrompt.allTime.total)}
                      </span>
                      <span className="text-[11px] text-muted-foreground">
                        {spending.smartPrompt.allTime.count} {spending.smartPrompt.allTime.count === 1 ? "analiza" : "analiza"}
                      </span>
                    </div>
                    {Object.entries(spending.smartPrompt.allTime.byModel).map(([model, stats]) => (
                      <div key={model} className="flex items-center justify-between text-[10px]">
                        <span className="text-muted-foreground">{model}</span>
                        <span className="text-foreground/70 tabular-nums">
                          {formatCost(stats.total)} ({stats.count})
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Admin: Per User section */}
              {isAdmin && adminSpending && (
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                    <Users className="w-3.5 h-3.5" />
                    Po korisniku (ukupno)
                  </Label>
                  <div className="space-y-2">
                    {Object.entries(adminSpending).map(([userId, userData]) => {
                      const allTime = userData.spending.allTime;
                      const modelEntries = Object.entries(allTime.byModel);
                      return (
                        <div
                          key={userId}
                          className="rounded-xl border border-border bg-secondary/20 p-3"
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium">{userData.username}</span>
                            <div className="flex items-baseline gap-2">
                              <span className="text-sm font-bold text-primary tabular-nums">
                                {formatCost(allTime.total)}
                              </span>
                              <span className="text-[11px] text-muted-foreground">
                                {allTime.count} {allTime.count === 1 ? "slika" : "slika"}
                              </span>
                            </div>
                          </div>
                          {modelEntries.length > 0 && (
                            <div className="space-y-0.5 pt-1.5 border-t border-border/50">
                              {modelEntries.map(([modelKey, modelStats]) => (
                                <div key={modelKey} className="flex items-center justify-between text-[10px]">
                                  <span className="text-muted-foreground truncate mr-2">
                                    {getModelDisplayName(modelKey)}
                                  </span>
                                  <span className="text-foreground/70 tabular-nums shrink-0">
                                    {formatCost(modelStats.total)} ({modelStats.count})
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <p className="text-sm">Greška pri učitavanju podataka o potrošnji.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
